import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import { ApiError, updateLesson } from '@platform/client';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';

/** A lesson about to be published or moved to draft, named the way its screen names it. */
export interface PublishTarget {
  lesson: string;
  action: 'publish' | 'unpublish';
  name: string;
  /** Where it sits, when the screen opening the dialog knows. */
  place?: string;
  /** Why the screen asked, when the admin did not press Publish or Move to draft. */
  why?: string;
}

export const PUBLISH_LABEL: Record<PublishTarget['action'], string> = {
  publish: 'Publish',
  unpublish: 'Move to draft',
};

const ROW_ACTION = {
  DRAFT: 'publish',
  PUBLISHED: 'unpublish',
  ARCHIVED: undefined,
} as const;

/**
 * A table row's Publish or Move to draft — ADMIN-28 on lessons, ADMIN-29 on courses and
 * modules. Whether it may go live is the API's answer, shown in the dialog it opens.
 */
export function RowPublishCell({
  status,
  title,
  onClick,
}: {
  status: keyof typeof ROW_ACTION;
  title: string;
  onClick: (action: PublishTarget['action']) => void;
}) {
  const action = ROW_ACTION[status];

  return (
    <TableCell>
      {action !== undefined && (
        <Button
          size="small"
          sx={{ whiteSpace: 'nowrap' }}
          aria-label={`${PUBLISH_LABEL[action]} ${title}`}
          onClick={() => {
            onClick(action);
          }}
        >
          {PUBLISH_LABEL[action]}
        </Button>
      )}
    </TableCell>
  );
}

/**
 * Publish puts a lesson in front of learners and Move to draft takes it away, so both
 * name what they act on first — CURR-22's locked proposal. Every reason the gate gives is
 * listed, not just the first. Shared by the curriculum table and the lesson page, which
 * has no row to name — GEN-118.
 */
export function PublishDialog({
  target,
  onClose,
  onDone,
}: {
  target: PublishTarget | null;
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const publishing = target?.action === 'publish';

  const write = useTrackedMutation(
    publishing ? 'Publish a lesson' : 'Move a lesson to draft',
    {
      mutationFn: async ({ lesson }: PublishTarget) =>
        updateLesson({
          path: { id: lesson },
          body: { status: publishing ? 'PUBLISHED' : 'DRAFT' },
          throwOnError: true,
        }),
      onSuccess: async () => {
        await onDone();
        onClose();
      },
    },
  );
  const close = (): void => {
    write.reset();
    onClose();
  };

  const reasons =
    write.error instanceof ApiError
      ? write.error.details.map((one) => one.message)
      : [];

  return (
    <Dialog open={target !== null} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>
        {publishing ? 'Publish this lesson?' : 'Move this lesson to draft?'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {target !== null && (
            <Typography>
              {target.name}
              {target.place !== undefined && (
                <>
                  <br />
                  {target.place}
                </>
              )}
            </Typography>
          )}
          {target?.why !== undefined && <Typography>{target.why}</Typography>}
          <Typography color="text.secondary">
            {publishing
              ? 'Learners will see it as soon as it is published.'
              : 'Learners will stop seeing it.'}
          </Typography>
          {write.isError && (
            <Alert severity="error">
              {describeApiError(write.error)}
              {reasons.length > 1 && (
                <Stack component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </Stack>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          disabled={target === null || write.isPending}
          onClick={() => {
            if (target !== null) write.mutate(target);
          }}
        >
          {PUBLISH_LABEL[target?.action ?? 'publish']}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
